import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Check, Crown, TrendingUp, Users, HardDrive, DollarSign, ArrowRight, 
  AlertCircle, AlertTriangle, Sparkles, Zap, Clock, CreditCard, 
  X, RefreshCw, Gift, ChevronRight, CheckCircle2, XCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';

const SubscriptionView = () => {
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [availableTiers, setAvailableTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [usageAlerts, setUsageAlerts] = useState([]);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFeedback, setCancelFeedback] = useState('');
  const [processing, setProcessing] = useState(false);

  const [paymentVerifying, setPaymentVerifying] = useState(false);

  // Function to poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentVerifying(false);
      toast.info('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/platform-subscription/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentVerifying(false);
        toast.success(`Payment successful! Your ${response.data.tier_name} subscription is now active.`);
        fetchData();
        // Dispatch event to refresh sidebar
        window.dispatchEvent(new CustomEvent('subscription-updated'));
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (response.data.status === 'expired') {
        setPaymentVerifying(false);
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setPaymentVerifying(false);
        toast.error('Error verifying payment. Please contact support.');
      }
    }
  };

  useEffect(() => {
    fetchData();
    
    // Check for success/cancel from Stripe redirect
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (params.get('success') === 'true' && sessionId) {
      // Poll for payment status
      setPaymentVerifying(true);
      toast.info('Verifying payment...');
      pollPaymentStatus(sessionId);
    } else if (params.get('canceled') === 'true') {
      toast.info('Payment canceled. Your subscription remains unchanged.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, tiersRes, alertsRes] = await Promise.all([
        axios.get(`${API}/subscription-tiers/clinic/current`),
        axios.get(`${API}/subscription-tiers/public`),
        axios.get(`${API}/subscription-tiers/clinic/usage-alerts`).catch(() => ({ data: { alerts: [] } }))
      ]);
      
      setCurrentSubscription(subRes.data);
      setAvailableTiers(tiersRes.data);
      setUsageAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      toast.error('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTier = async (tier) => {
    setSelectedTier(tier);
    
    // Fetch comparison data
    try {
      const response = await axios.get(`${API}/subscription-tiers/clinic/compare/${tier.id}`);
      setComparison(response.data);
      setCompareDialogOpen(true);
    } catch (error) {
      console.error('Failed to compare tiers:', error);
      // Show upgrade dialog anyway
      setUpgradeDialogOpen(true);
    }
  };

  const handleUpgrade = async () => {
    if (!selectedTier) return;
    setProcessing(true);

    try {
      // Use the recurring subscription endpoint for automatic billing
      const response = await axios.post(`${API}/stripe-recurring/create-subscription`, {
        tier_id: selectedTier.id,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        toast.info('Redirecting to Stripe for recurring subscription setup...');
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Failed to upgrade:', error);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.warnings) {
        toast.error(detail.message);
        detail.warnings.forEach(w => toast.warning(w));
      } else {
        toast.error(detail || 'Failed to initiate payment');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleStartTrial = async (tier) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/subscription-tiers/clinic/start-trial`, {
        tier_id: tier.id
      });
      toast.success(response.data.message);
      fetchData();
      // Dispatch event to notify other components to refresh
      window.dispatchEvent(new CustomEvent('subscription-updated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/subscription-tiers/clinic/cancel`, {
        reason: cancelReason,
        feedback: cancelFeedback
      });
      toast.success(response.data.message);
      setCancelDialogOpen(false);
      fetchData();
      
      // Dispatch event to notify other components to refresh
      window.dispatchEvent(new CustomEvent('subscription-updated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivate = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/subscription-tiers/clinic/reactivate`);
      toast.success(response.data.message);
      fetchData();
      
      // Dispatch event to notify other components to refresh
      window.dispatchEvent(new CustomEvent('subscription-updated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setProcessing(false);
    }
  };

  const getUsagePercent = (current, max) => {
    if (max === -1) return 0;
    return Math.min((current / max) * 100, 100);
  };

  const getUsageColor = (percent) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 75) return 'bg-yellow-500';
    return 'bg-teal-500';
  };

  const currentTier = currentSubscription?.tier;
  const usage = currentSubscription?.usage || {};
  const subscription = currentSubscription?.subscription || {};
  const isTrial = currentSubscription?.is_trial;
  const isCanceled = subscription.cancel_at_period_end;
  
  // Calculate subscription status for Purchase/Renew buttons
  const daysRemaining = currentSubscription?.days_remaining;
  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 14 && daysRemaining > 0;
  const isExpired = daysRemaining !== null && daysRemaining <= 0;
  const trialExpired = currentSubscription?.trial_expired;
  const requiresPayment = trialExpired || isExpired || (!isTrial && !subscription?.status);
  const subscriptionActive = subscription?.status === 'active' && !isExpired;

  // Handler for recurring subscription payment
  const handleRecurringPayment = async (tier) => {
    if (!tier) return;
    setSelectedTier(tier);
    setProcessing(true);

    try {
      // Use the recurring subscription endpoint
      const response = await axios.post(`${API}/stripe-recurring/create-subscription`, {
        tier_id: tier.id,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        toast.info('Redirecting to Stripe for subscription setup...');
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  // Handler for one-time payment (no auto-renewal)
  const handleOneTimePayment = async (tier) => {
    if (!tier) return;
    setSelectedTier(tier);
    setProcessing(true);

    try {
      // Use the platform subscription endpoint for one-time payment
      const response = await axios.post(`${API}/platform-subscription/create-checkout`, {
        tier_id: tier.id,
        billing_cycle: billingCycle,
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        toast.info('Redirecting to Stripe for payment...');
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  // Handler for direct purchase/renew (default to recurring)
  const handleDirectPayment = async (tier) => {
    await handleRecurringPayment(tier);
  };

  if (loading || paymentVerifying) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        {paymentVerifying && (
          <p className="text-gray-600">Verifying your payment...</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscription</h1>
          <p className="text-gray-600 mt-1">Manage your plan and billing</p>
        </div>
        {currentTier && currentTier.slug !== 'starter' && !isCanceled && (
          <Button variant="outline" onClick={() => setCancelDialogOpen(true)} className="text-gray-600">
            Cancel Subscription
          </Button>
        )}
      </div>

      {/* Usage Alerts */}
      {usageAlerts.length > 0 && (
        <div className="space-y-2">
          {usageAlerts.map((alert, idx) => (
            <div 
              key={idx}
              className={`flex items-center gap-3 p-4 rounded-lg ${
                alert.severity === 'critical' ? 'bg-red-50 border border-red-200' :
                alert.severity === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
                'bg-blue-50 border border-blue-200'
              }`}
            >
              <AlertTriangle className={`h-5 w-5 ${
                alert.severity === 'critical' ? 'text-red-500' :
                alert.severity === 'warning' ? 'text-yellow-500' :
                'text-blue-500'
              }`} />
              <div className="flex-1">
                <p className="font-medium text-gray-900">{alert.message}</p>
                <p className="text-sm text-gray-600">{alert.action}</p>
              </div>
              <Button size="sm" onClick={() => setUpgradeDialogOpen(true)}>
                <Zap className="h-4 w-4 mr-1" />
                Upgrade
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Trial Banner */}
      {isTrial && currentSubscription?.trial_ends_at && (
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-full">
                  <Gift className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Free Trial Active</p>
                  <p className="text-sm text-gray-600">
                    Your {currentTier?.name} trial ends on {new Date(currentSubscription.trial_ends_at).toLocaleDateString()}
                    {currentSubscription.trial_days_remaining > 0 && currentSubscription.trial_days_remaining <= 7 && (
                      <span className="text-red-600 font-medium ml-1">
                        ({currentSubscription.trial_days_remaining} days left!)
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => handleSelectTier(currentTier)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Subscribe Now
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancellation Banner */}
      {isCanceled && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="font-semibold text-gray-900">Subscription Ending</p>
                  <p className="text-sm text-gray-600">
                    Your subscription will end on {new Date(subscription.current_period_end).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <Button onClick={handleReactivate} disabled={processing}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reactivate
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Required Banner - Trial Expired or No Active Subscription */}
      {!isTrial && requiresPayment && !isCanceled && (
        <Card className="border-red-300 bg-gradient-to-r from-red-50 to-orange-50">
          <CardContent className="py-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="font-bold text-lg text-gray-900">Payment Required</p>
                  <p className="text-sm text-gray-600">
                    {trialExpired 
                      ? 'Your free trial has ended. Subscribe now to continue using all features.'
                      : 'Your subscription has expired. Renew now to restore access to all features.'
                    }
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  size="lg"
                  onClick={() => currentTier && handleRecurringPayment(currentTier)}
                  disabled={processing || !currentTier}
                  className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 shadow-lg"
                >
                  <RefreshCw className="h-5 w-5 mr-2" />
                  {trialExpired ? 'Subscribe (Auto-renew)' : 'Renew (Auto-renew)'}
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => currentTier && handleOneTimePayment(currentTier)}
                  disabled={processing || !currentTier}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  <CreditCard className="h-5 w-5 mr-2" />
                  Pay Once
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Renewal Reminder Banner - Subscription expiring soon */}
      {!isTrial && isExpiringSoon && subscriptionActive && !isCanceled && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-full">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Subscription Renewing Soon</p>
                  <p className="text-sm text-gray-600">
                    Your {currentTier?.name} subscription will renew in <span className="font-medium text-amber-700">{daysRemaining} days</span>.
                    {subscription.billing_cycle === 'monthly' && (
                      <span className="ml-1">Switch to annual billing to save 20%!</span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {subscription.billing_cycle === 'monthly' && (
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setBillingCycle('annual');
                      currentTier && handleDirectPayment(currentTier);
                    }}
                    disabled={processing}
                    className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Switch to Annual
                  </Button>
                )}
                <Button 
                  onClick={() => currentTier && handleDirectPayment(currentTier)}
                  disabled={processing || !currentTier}
                  className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Renew Early
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Current Plan & Usage */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Current Plan */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-500" />
                Current Plan
              </CardTitle>
              {currentTier && (
                <Badge className={`${
                  currentTier.slug === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                  currentTier.slug === 'professional' ? 'bg-blue-100 text-blue-700' :
                  currentTier.slug === 'growth' ? 'bg-green-100 text-green-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {currentTier.name}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {currentTier ? (
              <>
                <div className="text-3xl font-bold text-gray-900">
                  ${currentTier.monthly_price}
                  <span className="text-sm font-normal text-gray-500">/month</span>
                </div>
                
                {currentTier.annual_price > 0 && (
                  <p className="text-sm text-green-600">
                    or ${currentTier.annual_price}/mo billed annually (save 20%)
                  </p>
                )}

                <div className="pt-4 space-y-2">
                  {currentSubscription?.days_remaining > 0 && subscriptionActive && (
                    <>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="h-4 w-4" />
                        <span>{currentSubscription.days_remaining} days until renewal</span>
                      </div>
                      {subscription.current_period_end && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span className="ml-6">Renews on {new Date(subscription.current_period_end).toLocaleDateString('en-US', { 
                            month: 'long', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}</span>
                        </div>
                      )}
                    </>
                  )}
                  {subscription.plan_price && subscriptionActive && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <CreditCard className="h-4 w-4" />
                      <span>Billed ${subscription.plan_price}/{subscription.billing_cycle || 'month'}</span>
                    </div>
                  )}
                  {requiresPayment && (
                    <div className="flex items-center gap-2 text-sm text-red-600 font-medium">
                      <AlertCircle className="h-4 w-4" />
                      <span>{trialExpired ? 'Trial expired - Payment required' : 'Subscription expired'}</span>
                    </div>
                  )}
                </div>

                {/* Purchase/Renew Button in Current Plan Card */}
                {requiresPayment && (
                  <Button 
                    className="w-full mt-4 bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
                    onClick={() => handleDirectPayment(currentTier)}
                    disabled={processing}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {trialExpired ? 'Purchase Now' : 'Renew Now'}
                  </Button>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-600">No active subscription</p>
                <Button className="mt-4" onClick={() => setUpgradeDialogOpen(true)}>
                  Choose a Plan
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-teal-600" />
              Usage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Members */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-500" />
                  Members
                </span>
                <span>
                  {usage.current_members || 0} / {currentTier?.max_members === -1 ? '∞' : currentTier?.max_members || 0}
                </span>
              </div>
              <Progress 
                value={getUsagePercent(usage.current_members, currentTier?.max_members)} 
                className="h-2"
              />
            </div>

            {/* Staff */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-500" />
                  Staff Users
                </span>
                <span>
                  {usage.current_staff || 0} / {currentTier?.max_users === -1 ? '∞' : currentTier?.max_users || 0}
                </span>
              </div>
              <Progress 
                value={getUsagePercent(usage.current_staff, currentTier?.max_users)} 
                className="h-2"
              />
            </div>

            {/* Storage */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2">
                  <HardDrive className="h-4 w-4 text-gray-500" />
                  Storage
                </span>
                <span>
                  {(usage.current_storage_gb || 0).toFixed(1)}GB / {currentTier?.max_storage_gb === -1 ? '∞' : currentTier?.max_storage_gb || 0}GB
                </span>
              </div>
              <Progress 
                value={getUsagePercent(usage.current_storage_gb, currentTier?.max_storage_gb)} 
                className="h-2"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Available Plans */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle>Available Plans</CardTitle>
              <CardDescription>Choose the plan that fits your needs</CardDescription>
            </div>
            <Tabs value={billingCycle} onValueChange={setBillingCycle}>
              <TabsList>
                <TabsTrigger value="monthly">Monthly</TabsTrigger>
                <TabsTrigger value="annual" className="flex items-center gap-1">
                  Annual
                  <Badge className="bg-green-100 text-green-700 text-xs">-20%</Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {availableTiers.map((tier) => {
              const isCurrentPlan = currentTier?.id === tier.id;
              const price = billingCycle === 'annual' ? tier.annual_price : tier.monthly_price;
              const monthlyEquivalent = billingCycle === 'annual' ? Math.round(tier.annual_price / 12) : tier.monthly_price;
              
              return (
                <div
                  key={tier.id}
                  className={`relative flex flex-col p-6 rounded-xl border-2 transition-all h-full ${
                    isCurrentPlan 
                      ? 'border-teal-500 bg-teal-50' 
                      : tier.is_popular 
                        ? 'border-purple-300 bg-purple-50/50' 
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {tier.is_popular && !isCurrentPlan && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 whitespace-nowrap">
                      Most Popular
                    </Badge>
                  )}
                  
                  {isCurrentPlan && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-600 whitespace-nowrap">
                      Current Plan
                    </Badge>
                  )}

                  {/* Plan Name */}
                  <div className="pt-2">
                    <h3 className="font-bold text-lg text-gray-900">{tier.name}</h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2 min-h-[40px]">{tier.description}</p>
                  </div>
                  
                  {/* Price */}
                  <div className="mt-4 pb-4 border-b">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-gray-900">
                        ${billingCycle === 'annual' ? tier.annual_price : tier.monthly_price}
                      </span>
                      <span className="text-gray-500 text-sm">/mo</span>
                    </div>
                    {billingCycle === 'annual' && (
                      <p className="text-xs text-green-600 mt-1">
                        Billed ${Math.round(tier.annual_price * 12)}/year (Save 20%)
                      </p>
                    )}
                    {billingCycle === 'monthly' && tier.annual_price && (
                      <p className="text-xs text-gray-500 mt-1">
                        or ${tier.annual_price}/mo billed annually
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="mt-4 space-y-3 text-sm flex-grow">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>{tier.max_members === -1 ? 'Unlimited' : tier.max_members} members</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>{tier.max_users === -1 ? 'Unlimited' : tier.max_users} staff users</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>{tier.max_storage_gb === -1 ? 'Unlimited' : tier.max_storage_gb}GB storage</span>
                    </li>
                    {tier.custom_branding && (
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        <span>Custom branding</span>
                      </li>
                    )}
                    {tier.advanced_analytics && (
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        <span>Advanced analytics</span>
                      </li>
                    )}
                    {tier.priority_support && (
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        <span>Priority support</span>
                      </li>
                    )}
                  </ul>

                  {/* Action Buttons */}
                  <div className="mt-6 pt-4 border-t space-y-2">
                    {isCurrentPlan && subscriptionActive ? (
                      <Button disabled className="w-full" variant="outline">
                        <Check className="h-4 w-4 mr-2" />
                        Current Plan
                      </Button>
                    ) : isCurrentPlan && requiresPayment ? (
                      <>
                        {/* Recurring subscription - Primary option */}
                        <Button 
                          className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
                          onClick={() => handleRecurringPayment(tier)}
                          disabled={processing}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          {trialExpired ? 'Subscribe (Auto-renew)' : 'Renew (Auto-renew)'}
                        </Button>
                        {/* One-time payment - Secondary option */}
                        <Button 
                          variant="outline"
                          className="w-full"
                          onClick={() => handleOneTimePayment(tier)}
                          disabled={processing}
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          {trialExpired ? 'Pay Once (No auto-renew)' : 'Pay Once'}
                        </Button>
                      </>
                    ) : requiresPayment ? (
                      <>
                        {/* Recurring subscription - Primary option */}
                        <Button 
                          className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                          onClick={() => handleRecurringPayment(tier)}
                          disabled={processing}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Subscribe (Auto-renew)
                        </Button>
                        {/* One-time payment - Secondary option */}
                        <Button 
                          variant="outline"
                          className="w-full"
                          onClick={() => handleOneTimePayment(tier)}
                          disabled={processing}
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Pay Once (No auto-renew)
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button 
                          className="w-full"
                          onClick={() => handleSelectTier(tier)}
                          disabled={processing}
                        >
                          {price > (currentTier?.monthly_price || 0) ? (
                            <>
                              Upgrade
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </>
                          ) : (
                            <>
                              Switch
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </>
                          )}
                        </Button>
                        {price > (currentTier?.monthly_price || 0) && tier.slug !== 'enterprise' && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="w-full text-purple-600 hover:text-purple-700"
                            onClick={() => handleStartTrial(tier)}
                            disabled={processing}
                          >
                            <Gift className="h-4 w-4 mr-1" />
                            14-day free trial
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Compare Dialog */}
      <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {comparison?.is_upgrade ? 'Upgrade' : 'Switch'} to {selectedTier?.name}
            </DialogTitle>
            <DialogDescription>
              Review the changes before confirming
            </DialogDescription>
          </DialogHeader>

          {comparison && (
            <div className="space-y-4">
              {/* Price Change */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-gray-600">Price change</span>
                <span className={`font-bold ${comparison.price_difference > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {comparison.price_difference > 0 ? '+' : ''}${comparison.price_difference}/mo
                </span>
              </div>

              {/* Proration */}
              {comparison.proration_amount > 0 && (
                <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <span className="text-gray-600">Prorated charge (today)</span>
                  <span className="font-bold">${comparison.proration_amount}</span>
                </div>
              )}

              {/* Features Gained */}
              {comparison.features_gained.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-green-700 mb-2">What you get:</p>
                  <ul className="space-y-1">
                    {comparison.features_gained.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle2 className="h-4 w-4" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Features Lost */}
              {comparison.features_lost.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2">What you lose:</p>
                  <ul className="space-y-1">
                    {comparison.features_lost.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-red-600">
                        <XCircle className="h-4 w-4" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCompareDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpgrade} disabled={processing}>
              {processing ? 'Processing...' : comparison?.is_upgrade ? 'Upgrade Now' : 'Switch Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              We are sorry to see you go. Your subscription will remain active until the end of your billing period.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Why are you canceling? (optional)</Label>
              <select 
                className="w-full mt-1 p-2 border rounded-lg"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              >
                <option value="">Select a reason...</option>
                <option value="too_expensive">Too expensive</option>
                <option value="not_using">Not using it enough</option>
                <option value="missing_features">Missing features I need</option>
                <option value="found_alternative">Found an alternative</option>
                <option value="temporary">Just need a break</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <Label>Any feedback for us? (optional)</Label>
              <Textarea
                placeholder="Help us improve..."
                value={cancelFeedback}
                onChange={(e) => setCancelFeedback(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              Keep Subscription
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={processing}>
              {processing ? 'Canceling...' : 'Confirm Cancellation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionView;
